import { useEffect, useState } from "react";
import { Bien } from "../../model/Model";
import { getBienById

 } from "../../service/biens/biens.service";

interface AjouterBienPopupProps {
    ajouterModifier: (bien: Bien) => Promise<void>;
    onClose?: () => void;
    bienId: number | undefined;
}
 
export function AjouterBienPopup({ ajouterModifier , onClose, bienId }: AjouterBienPopupProps) {
    const [bien, setBien] = useState<Bien>({
        adresse: "",
        ville: "",
        codePostal: "",
        type: "",
        superficie: 0,
        nombreDePieces: 0,
        classeEnergetique: "",
        description: "",
        identifiant: ""
    });

    useEffect(()=>{
        const fetchBien = async () => {
            if(!bienId) return;
            const data = await getBienById(bienId);
            if (data) {
                setBien(data);
            }
        };
        fetchBien();
    },[bienId])

    async function handleAjouter(e: React.FormEvent) {
        e.preventDefault();
        await ajouterModifier(bien);
        console.log("bien ajouté");
        // Reset form
        setBien({
            adresse: "",
            ville: "",
            codePostal: "",
            type: "",
            superficie: 0,
            nombreDePieces: 0,
            classeEnergetique: "",
            description: "",
            identifiant: ""
        });
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setBien(prev => ({
            ...prev,
            [name]: name === 'superficie' || name === 'nombreDePieces' || name === 'proprietaireId' 
                ? Number(value) 
                : value
        }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Ajouter un bien</h2>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>

                <form onSubmit={handleAjouter} className="space-y-6">
                    {/* Informations de base */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Identifiant du bien
                            </label>
                            <input
                                type="text"
                                name="identifiant"
                                value={bien.identifiant}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Ex: BIEN001"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Type de bien *
                            </label>
                            <select
                                name="type"
                                value={bien.type}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Sélectionnez un type</option>
                                <option value="Appartement">Appartement</option>
                                <option value="Maison">Maison</option>
                                <option value="Studio">Studio</option>
                                <option value="Loft">Loft</option>
                                <option value="Duplex">Duplex</option>
                                <option value="Villa">Villa</option>
                                <option value="Local commercial">Local commercial</option>
                                <option value="Bureau">Bureau</option>
                            </select>
                        </div>
                    </div>

                    {/* Adresse complète */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Adresse *
                        </label>
                        <input
                            type="text"
                            name="adresse"
                            value={bien.adresse}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Numéro et nom de rue"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Ville *
                            </label>
                            <input
                                type="text"
                                name="ville"
                                value={bien.ville}
                                onChange={handleChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Nom de la ville"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Code postal *
                            </label>
                            <input
                                type="text"
                                name="codePostal"
                                value={bien.codePostal}
                                onChange={handleChange}
                                required
                                pattern="[0-9]{5}"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="12345"
                            />
                        </div>
                    </div>

                    {/* Caractéristiques */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Superficie (m²) *
                            </label>
                            <input
                                type="number"
                                name="superficie"
                                value={bien.superficie}
                                onChange={handleChange}
                                required
                                min="1"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="50"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Nombre de pièces *
                            </label>
                            <input
                                type="number"
                                name="nombreDePieces"
                                value={bien.nombreDePieces}
                                onChange={handleChange}
                                required
                                min="1"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="3"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Classe énergétique
                            </label>
                            <select
                                name="classeEnergetique"
                                value={bien.classeEnergetique}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Non définie</option>
                                <option value="A">A (Très économe)</option>
                                <option value="B">B (Économe)</option>
                                <option value="C">C (Assez économe)</option>
                                <option value="D">D (Assez peu économe)</option>
                                <option value="E">E (Peu économe)</option>
                                <option value="F">F (Très peu économe)</option>
                                <option value="G">G (Extrêmement peu économe)</option>
                            </select>
                        </div>
                    </div>

                    
                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                        </label>
                        <textarea
                            name="description"
                            value={bien.description}
                            onChange={handleChange}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Description détaillée du bien (équipements, état, particularités...)"
                        />
                    </div>

                    {/* Boutons */}
                    <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                        {onClose && (
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition duration-200"
                            >
                                Annuler
                            </button>
                        )}
                        { bienId ? (
                            <button
                                type="submit"
                                className="px-6 py-2 text-sm font-medium text-white bg-yellow-700 hover:bg-yellow-700 rounded-md transition duration-200 transform hover:scale-105 shadow-md"
                            >
                                Modifier le bien
                            </button>
                        ) : (
                            <button
                                type="submit"
                                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition duration-200 transform hover:scale-105 shadow-md"
                            >
                                Ajouter le bien
                            </button>
                        )}
                        
                    </div>
                </form>
            </div>
        </div>
    );
}